import React from 'react';
import { Download, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useSpin } from '../../context/SpinContext';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Progress } from '../ui/progress';
import { ASPECT_PRESETS } from '../../lib/exportConstants';

export default function ExportControls({ exportJob }) {
  const { exportSettings, updateExport, disc, audio } = useSpin();
  const { status, phaseLabel, pct, errorMsg, canExport, handleExport } = exportJob;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-neutral-600 mb-2">Output</label>
        <ToggleGroup
          type="single"
          value={exportSettings.aspect}
          onValueChange={(v) => v && updateExport({ aspect: v })}
          className="grid grid-cols-1 gap-2 w-full"
        >
          {Object.entries(ASPECT_PRESETS).map(([id, a]) => (
            <ToggleGroupItem key={id} value={id} className="justify-start px-3 border border-neutral-200 rounded-lg data-[state=on]:bg-orange-50 data-[state=on]:border-orange-300 data-[state=on]:text-orange-700 text-sm">
              {a.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <p className="text-[11px] text-neutral-400 mt-1.5">
          Frame rate is automatic: 30fps at or below 120 RPM, 60fps above it (reduces wagon-wheel aliasing).
        </p>
      </div>

      <div className="pt-1">
        {status === 'exporting' ? (
          <div className="space-y-2.5 rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-3">
            <div className="flex items-center gap-2 text-sm text-neutral-700">
              <Loader2 className="w-4 h-4 animate-spin text-orange-600" />
              <span>{phaseLabel}</span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="text-[11px] text-neutral-400">{Math.round(pct)}%</div>
            <p className="text-[11px] text-neutral-400">Rendering happens on the server — feel free to wait, the job keeps running either way.</p>
          </div>
        ) : status === 'error' ? (
          <div className="space-y-2.5 rounded-lg bg-red-50 border border-red-200 px-3 py-3">
            <div className="flex items-start gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
            <button
              type="button"
              onClick={handleExport}
              className="text-xs font-medium text-red-700 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        ) : status === 'done' ? (
          <div className="space-y-2.5 rounded-lg bg-green-50 border border-green-200 px-3 py-3">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="w-4 h-4" /> Video downloaded successfully.
            </div>
            <button
              type="button"
              onClick={handleExport}
              className="flex items-center justify-center gap-2 w-full h-10 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" /> Export again
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              data-testid="desktop-export-video-button"
              onClick={handleExport}
              disabled={!canExport}
              className="flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:bg-neutral-200 disabled:text-neutral-400 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
            >
              <Download className="w-4 h-4" /> Export video
            </button>
            {!canExport && (
              <p className="text-xs text-neutral-400 mt-2">
                {!disc.imageEl
                  ? 'Upload disc art to export.'
                  : (!audio.noAudio && !audio.hasAudio)
                    ? 'Upload an audio track to export (or check "Generate without audio").'
                    : 'Set a video length to export.'}
              </p>
            )}
            <p className="text-xs text-neutral-400 mt-2 leading-relaxed">
              Rendered server-side with ffmpeg (H.264 + AAC) and downloaded to your device — ready for Instagram, TikTok, or YouTube.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
